/**
 * Lire un `ErrorEvent` sans se laisser prendre par « Script error. ».
 *
 * Quand une exception est levée par un script servi depuis une **autre
 * origine** sans en-tête CORS, le navigateur refuse de la décrire à la page :
 * le message devient `"Script error."`, `filename` et `lineno` sont vidés, et
 * `error` — donc la pile — vaut `null`. C'est une protection du navigateur,
 * pas une panne : rien de ce qui suit n'est récupérable côté client.
 *
 * L'événement remonté est alors vide de sens. Il portait pourtant `fatal:
 * true` et se rangeait avec les vraies casses, où il ne peut ni se trier ni se
 * reproduire, et où il gonfle le seul chiffre qu'on regarde pour savoir si le
 * produit tient debout.
 *
 * Il vaut mieux le garder que le jeter — un tiers qui échoue en boucle mérite
 * d'être compté — mais il doit se distinguer d'un coup d'œil, d'où `opaque`.
 *
 * **Et un `opaque` ne vient jamais du code de Virtuosa.** Tout ce que la page
 * charge est de même origine : les fragments Next, les polices `next/font`
 * (recopiées à la construction), aucun `<script>` externe, aucun CDN, aucun
 * worker. Une erreur de notre code arrive donc toujours avec son message et sa
 * pile — `productionBrowserSourceMaps` est actif. Un `opaque` désigne du code
 * injecté dans la page : un navigateur applicatif qui pose son propre script,
 * une extension, un bloqueur.
 */

export type UncaughtErrorReport = {
  /** Le message tel que le navigateur l'a donné, jamais réécrit. */
  readonly message: string;
  /** La tête de la pile, absente quand le navigateur ne la livre pas. */
  readonly stackHead?: string;
  /** Vrai quand le navigateur a masqué l'origine de l'erreur. */
  readonly opaque: boolean;
};

/** Ce que cette lecture demande d'un `ErrorEvent`, et rien de plus. */
export type UncaughtErrorLike = {
  readonly message?: unknown;
  readonly filename?: unknown;
  readonly error?: unknown;
};

const MESSAGE_LIMIT = 300;
const STACK_LIMIT = 500;

/**
 * On reconnaît l'opacité à sa **forme**, pas à son texte : ni objet d'erreur,
 * ni fichier. Se fier au libellé exact reviendrait à parier sur l'orthographe
 * d'un navigateur — WebKit, Gecko et Blink ne s'accordent déjà pas sur le
 * point final.
 */
export function describeUncaughtError(event: UncaughtErrorLike): UncaughtErrorReport {
  const error = event.error;
  const filename = typeof event.filename === "string" ? event.filename : "";

  return {
    message: String(event.message).slice(0, MESSAGE_LIMIT),
    stackHead: error instanceof Error ? (error.stack ?? "").slice(0, STACK_LIMIT) : undefined,
    opaque: !(error instanceof Error) && filename === "",
  };
}
